import type { ApiErrorBody, ApiErrorDetail, ErrorCode, PaginationMeta } from '@tms/shared';

const API_BASE = '/api/v1';

/** Broadcast when the API rejects a request for lack of a valid session. */
export const UNAUTHENTICATED_EVENT = 'tms:unauthenticated';

/**
 * Error carrying the API's error envelope. Every UI surface renders
 * `error.message` and, for forms, maps `error.details` onto fields.
 */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: ApiErrorDetail[];
  readonly requestId?: string;

  constructor(init: {
    code: ErrorCode;
    message: string;
    status: number;
    details?: ApiErrorDetail[];
    requestId?: string;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.details = init.details ?? [];
    if (init.requestId) this.requestId = init.requestId;
  }

  get isValidation(): boolean {
    return this.code === 'VALIDATION_ERROR' || this.code === 'CONFLICT';
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Sent as-is with the blob's own content type — used for image uploads. */
  rawBody?: Blob;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
}

const buildUrl = (path: string, query?: RequestOptions['query']): string => {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    url.searchParams.set(key, String(value));
  }
  return `${url.pathname}${url.search}`;
};

/**
 * A cancelled request must be re-thrown as-is so the caller (TanStack Query)
 * can recognise it as a cancellation. The check is by `name` rather than
 * `instanceof DOMException`, because the DOMException class the runtime throws
 * is not always the same class the document sees.
 */
const isAbortError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { name?: unknown }).name === 'AbortError';

const networkError = (): ApiError =>
  new ApiError({
    code: 'NETWORK_ERROR',
    message: 'Cannot reach the server. Check your connection and try again.',
    status: 0,
  });

const parseError = async (response: Response): Promise<ApiError> => {
  let body: ApiErrorBody | null = null;
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    body = null;
  }

  const error = body?.error;
  return new ApiError({
    code: error?.code ?? 'INTERNAL_ERROR',
    message: error?.message ?? 'Unexpected server response. Please try again.',
    status: response.status,
    ...(error?.details ? { details: error.details } : {}),
    ...(error?.requestId ? { requestId: error.requestId } : {}),
  });
};

/**
 * The single place the browser talks to the API. Cookies are sent with every
 * request; a 401 additionally notifies the app so it can return to the login
 * screen instead of showing a broken page.
 */
const request = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const { method = 'GET', body, rawBody, query, signal } = options;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      credentials: 'same-origin',
      ...(rawBody
        ? { headers: { 'content-type': rawBody.type }, body: rawBody }
        : {
            headers: body === undefined ? {} : { 'content-type': 'application/json' },
            ...(body === undefined ? {} : { body: JSON.stringify(body) }),
          }),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    if (isAbortError(cause)) throw cause;
    throw networkError();
  }

  if (response.status === 401) {
    window.dispatchEvent(new CustomEvent(UNAUTHENTICATED_EVENT));
    throw await parseError(response);
  }

  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;

  const payload = (await response.json()) as { data: T };
  return payload.data;
};

export interface ListResponse<T> {
  data: T[];
  meta: PaginationMeta;
}

/** Same as `request`, but keeps the pagination meta alongside the rows. */
const requestList = async <T>(
  path: string,
  options: RequestOptions = {},
): Promise<ListResponse<T>> => {
  const { query, signal } = options;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method: 'GET',
      credentials: 'same-origin',
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    if (isAbortError(cause)) throw cause;
    throw networkError();
  }

  if (response.status === 401) {
    window.dispatchEvent(new CustomEvent(UNAUTHENTICATED_EVENT));
    throw await parseError(response);
  }
  if (!response.ok) throw await parseError(response);

  return (await response.json()) as ListResponse<T>;
};

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', ...(query ? { query } : {}), ...(signal ? { signal } : {}) }),
  list: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    requestList<T>(path, { ...(query ? { query } : {}), ...(signal ? { signal } : {}) }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  /** Uploads a file as the request body (see the attachments endpoint). */
  upload: <T>(path: string, file: Blob, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'POST', rawBody: file, ...(query ? { query } : {}) }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T = void>(path: string, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'DELETE', ...(query ? { query } : {}) }),
};
